// Client-side routing for /dashboard.
//
// The page used to be server-rendered, so every tab click was a full navigation: load the
// HTML, then fetch the rows. The HTML round trip bought nothing — two of the tabs run no
// server queries at all — and it reset the scroll position on every click.
//
// Only this page is client-routed. Everything else in the app is still SSR, so the
// delegator's `navigate` handler stays a plain `location.href` and consults the handler
// registered here first (see clientRoute.ts).
import { dashboardRoute, type DashboardRoute } from "./route";

/** Renders the route. Returns once the chrome is on screen; rows continue in background. */
type RouteRenderer = (route: DashboardRoute) => void;

let renderer: RouteRenderer | null = null;
let rendered: string | null = null;

/** `tab|subtab|filter` — what actually decides whether a re-render is needed. */
function routeKey(route: DashboardRoute): string {
  return `${route.tab}|${route.subtab}|${route.filter}`;
}

/**
 * Take over dashboard navigation. Called once, after the first render.
 *
 * `initialKey` is the route already on screen, so the first popstate for an unchanged URL
 * — which is what browser Back from an open modal produces — is a no-op rather than a
 * refetch of a 21-second tab.
 */
export function startRouter(render: RouteRenderer, initial: DashboardRoute): void {
  renderer = render;
  rendered = routeKey(initial);
  window.addEventListener("popstate", () => {
    if (!location.pathname.startsWith("/dashboard")) return;
    show(dashboardRoute(location.pathname));
  });
}

/**
 * Re-read the route on screen. For a tab whose rows just changed underneath it — an approval
 * decision overridden from the History tab — where "navigate to the same URL" is by design a
 * no-op in `show()`, and a fade-out of the row would hide a decision that still exists.
 */
export function refreshDashboardRoute(): void {
  if (!renderer || !location.pathname.startsWith("/dashboard")) return;
  renderer(dashboardRoute(location.pathname));
}

/** Render `route` if it differs from what is on screen. */
function show(route: DashboardRoute): void {
  const key = routeKey(route);
  if (!renderer || key === rendered) return;
  rendered = key;
  renderer(route);
}

/**
 * Handle an in-page navigation, or decline it.
 *
 * Declines anything that is not a dashboard URL, and anything at all before the dashboard
 * has mounted — so the same `data-action="navigate"` markup keeps working as a plain link
 * on every other page, and on this one until the bundle is ready.
 *
 * ⚠️ Modals push their own URLs and restore them with `replaceState`, so they never come
 * through here and never fire popstate on close. Browser Back from an open modal does fire
 * it, with the dashboard path already on screen — which `show()` correctly ignores.
 */
export function tryDashboardNavigate(href: string): boolean {
  if (!renderer) return false;
  if (!href.startsWith("/dashboard")) return false;
  // Same-document only. An absolute URL or one with a query string is somebody else's job.
  if (href.includes("://") || href.includes("?")) return false;

  const route = dashboardRoute(href);
  if (routeKey(route) === rendered) {
    /**
     * Clicking the tab you are already on RELOADS it.
     *
     * ⚠️ Deliberately different from `show()`, which ignores an unchanged route. That rule
     * exists for POPSTATE — browser Back from a closed modal arrives with the dashboard
     * path already on screen, and refetching a 21-second tab for it would be absurd. A
     * CLICK is not that: somebody pressing the tab they are looking at is asking for it
     * again, and doing nothing reads as a dead button.
     *
     * No `pushState`: the URL is already this one, and pushing it again would make Back
     * step through every refresh.
     *
     * No `scrollTo` either. A tab swap is a new page and starts at the top; a refresh is
     * the same page, and throwing the reader back to the top of a list they had scrolled
     * into is the opposite of what they asked for.
     */
    renderer(route);
    return true;
  }
  window.history.pushState(null, "", href);
  show(route);
  // A tab swap is a new page as far as the reader is concerned; the old scroll offset
  // belongs to markup that no longer exists.
  window.scrollTo(0, 0);
  return true;
}
